import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
    Mail,
    Calendar,
    Eye,
    CheckCircle,
    XCircle,
    UserCheck,
    Flag,
    FileText,
    Clock,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";
import { format } from "date-fns";

export default function OfficeLawyerRequests() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(null);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/requests/office-requests");
            if (res.data.success) {
                setRequests(res.data.data || []);
            }
        } catch (err) {
            Swal.fire("Error", "Failed to load office requests.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const openDetails = (request) => {
        setShowDetails(request);
    };

    const closeDetails = () => {
        setShowDetails(null);
    };

    const getStatusConfig = (status) => {
        const config = {
            pending: { color: "bg-yellow-100 text-yellow-800", icon: <Clock className="w-3 h-3" /> },
            accepted: { color: "bg-blue-100 text-blue-800", icon: <CheckCircle className="w-3 h-3" /> },
            assigned: { color: "bg-purple-100 text-purple-800", icon: <UserCheck className="w-3 h-3" /> },
            completed: { color: "bg-green-100 text-green-800", icon: <Flag className="w-3 h-3" /> },
            rejected: { color: "bg-red-100 text-red-800", icon: <XCircle className="w-3 h-3" /> },
        };
        return config[status] || config.pending;
    };

    if (loading) {
        return (
            <section className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-24 bg-white rounded-xl"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="p-6 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Office Requests Dashboard</h2>
                        <p className="text-gray-600 mt-1">Monitor all lawyer and paralegal requests</p>
                    </div>
                </div>

                {/* No Requests */}
                {requests.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                        <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FileText className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-gray-600 font-medium">No requests yet</p>
                        <p className="text-gray-500 text-sm mt-1">All office requests will appear here</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {requests.map((req, i) => {
                            const statusConfig = getStatusConfig(req.status);
                            return (
                                <motion.div
                                    key={req.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        {/* Client & Lawyer */}
                                        <div className="flex items-center space-x-4 flex-1">
                                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-bold text-sm">
                                                    {req.client_name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900">{req.client_name}</h4>
                                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                                    <Mail className="w-3 h-3" />
                                                    Lawyer: {req.lawyer_name}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Title & Case Type */}
                                        <div className="flex-1 text-center">
                                            <p className="font-medium text-gray-900">{req.title}</p>
                                            <p className="text-xs text-gray-600 mt-1">{req.case_type}</p>
                                        </div>

                                        {/* Assigned To */}
                                        <div className="flex-1 text-center">
                                            <p className="text-sm font-medium text-gray-900">
                                                {req.assigned_to_paralegal || "—"}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">Assigned To</p>
                                        </div>

                                        {/* Action */}
                                        <div className="flex items-center gap-2">
                                            {/* Files & Date */}
                                            <div className="flex-1 text-center">
                                                <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    <FileText className="w-3 h-3" />
                                                    {req.files_count} file{req.files_count !== 1 ? "s" : ""}
                                                </div>
                                            </div>

                                            {/* Status */}
                                            <div className="flex-1 text-center">
                                                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                                                    {statusConfig.icon}
                                                    {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => openDetails(req)}
                                                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                                title="View Details"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Details Modal */}
                {showDetails && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-xl font-bold text-gray-900">Request Details</h3>
                                <button
                                    onClick={closeDetails}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-5">
                                <div className="flex items-center space-x-4">
                                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-bold text-lg">
                                            {showDetails.client_name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{showDetails.client_name}</h4>
                                        <p className="text-sm text-gray-600">
                                            Lawyer: {showDetails.lawyer_name}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500">Title</p>
                                        <p className="font-medium text-gray-900">{showDetails.title}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Case Type</p>
                                        <p className="font-medium text-gray-900">{showDetails.case_type}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500">Status</p>
                                        <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusConfig(showDetails.status).color}`}>
                                            {getStatusConfig(showDetails.status).icon}
                                            {showDetails.status.charAt(0).toUpperCase() + showDetails.status.slice(1)}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Assigned To</p>
                                        <p className="font-medium text-gray-900">
                                            {showDetails.assigned_to_paralegal || "Not assigned"}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-gray-500">Submitted On</p>
                                        <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {format(new Date(showDetails.created_at), "MMMM d, yyyy 'at' h:mm a")}
                                        </p>
                                    </div>
                                    <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        <FileText className="w-3 h-3" />
                                        {showDetails.files_count} file{showDetails.files_count !== 1 ? "s" : ""}
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <button
                                        onClick={closeDetails}
                                        className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </div>
        </section>
    );
}